// Leaderboard reads. Hits the public `leaderboard_top` view (RLS allows
// SELECT to anon). Returns an empty array if the backend isn't configured.

import { getSupabase } from "./client";
import { getUserId } from "./auth";
import { fetchFriends } from "./friends";

export interface LeaderboardEntry {
  user_id:      string;
  display_name: string;
  avatar_id:    string | null;
  best_score:   number;
  best_time_ms: number;
  wins:         number;
}

export async function fetchLeaderboard(
  mapKey: string, difficulty: number, limit = 100,
): Promise<LeaderboardEntry[]> {
  const sb = getSupabase();
  if (!sb) return [];
  const { data, error } = await sb
    .from("leaderboard_top")
    .select("user_id, display_name, avatar_id, best_score, best_time_ms, wins")
    .eq("map_key", mapKey)
    .eq("difficulty", difficulty)
    .order("best_score", { ascending: false })
    .order("best_time_ms", { ascending: true })
    .limit(limit);
  if (error) {
    console.warn("[leaderboard] fetch failed", error);
    return [];
  }
  return (data ?? []) as LeaderboardEntry[];
}

export async function fetchFriendsLeaderboard(
  mapKey: string, difficulty: number, limit = 100,
): Promise<LeaderboardEntry[]> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return [];
  const friends = await fetchFriends();
  const allowedIds = Array.from(new Set([uid, ...friends.map((f) => f.userId)]));
  if (allowedIds.length === 0) return [];
  const { data, error } = await sb
    .from("leaderboard_top")
    .select("user_id, display_name, avatar_id, best_score, best_time_ms, wins")
    .eq("map_key", mapKey)
    .eq("difficulty", difficulty)
    .in("user_id", allowedIds)
    .order("best_score", { ascending: false })
    .order("best_time_ms", { ascending: true })
    .limit(limit);
  if (error) {
    console.warn("[leaderboard] friends fetch failed", error);
    return [];
  }
  return (data ?? []) as LeaderboardEntry[];
}

/** Convenience: caller's row in a leaderboard, or null if not signed in / no entry. */
export async function fetchMyEntry(
  mapKey: string, difficulty: number,
): Promise<LeaderboardEntry | null> {
  const sb = getSupabase();
  const uid = getUserId();
  if (!sb || !uid) return null;
  const { data, error } = await sb
    .from("leaderboard_top")
    .select("user_id, display_name, avatar_id, best_score, best_time_ms, wins")
    .eq("map_key", mapKey)
    .eq("difficulty", difficulty)
    .eq("user_id", uid)
    .maybeSingle();
  if (error) {
    console.warn("[leaderboard] fetchMy failed", error);
    return null;
  }
  return (data as LeaderboardEntry) ?? null;
}
